import { apiCreate, type RequestOptions } from './client'
import { findIncluded, relatedId } from './documents'
import {
  ACCESS_TOKENS_TYPE,
  type AccessTokenAttributes,
  type Document,
  type NewResource,
  type Resource,
  SESSIONS_TYPE,
  USERS_TYPE,
} from './types'
import { toUser, type User } from './users'

interface PasswordCredentials {
  /** One field in the UI; the API wants either `email` or `username`. */
  identifier: string
  password: string
}

interface ResetTokenCredentials {
  /**
   * The token from a password reset email. Single-use and good for an hour;
   * a spent, expired or mistyped one is a 404 the API does not distinguish.
   */
  token: string
}

interface OwnerCredentials {
  /**
   * A child the signed-in parent is signing in, by the ownership rule. The
   * request carries the parent's own session as `auth`; the reply is the child's.
   */
  childId: string
}

/**
 * What a session can be created with. The API also takes a badge and pin at
 * an organization, which nothing here uses.
 */
export type Credentials = PasswordCredentials | ResetTokenCredentials | OwnerCredentials

export interface SessionResult {
  accessToken: string
  expiresInSeconds: number
  userId: string
  user: User | null
}

/**
 * The design shows a single "Username or Email" field, so the client decides
 * which API attribute to use. An `@` is the only signal available, and it is
 * the same rule the API's own username parser implies (usernames are letters
 * followed by digits, so they can never contain one).
 */
export function isEmailIdentifier(identifier: string): boolean {
  return identifier.includes('@')
}

/** The resource `POST /sessions` takes for a credential. */
export function toSessionResource(credentials: Credentials): NewResource {
  if ('childId' in credentials) {
    return {
      type: SESSIONS_TYPE,
      relationships: { user: { data: { type: USERS_TYPE, id: credentials.childId } } },
    }
  }
  if ('token' in credentials) {
    return { type: SESSIONS_TYPE, attributes: { token: credentials.token } }
  }

  const identifier = credentials.identifier.trim()
  return {
    type: SESSIONS_TYPE,
    attributes: {
      ...(isEmailIdentifier(identifier) ? { email: identifier } : { username: identifier }),
      password: credentials.password,
    },
  }
}

/**
 * Pull the parts we need out of the session document. The access token and the
 * user are both in `included`; only the user id is on the session itself.
 */
export function normaliseSessionResponse(document: Document<Resource>): SessionResult {
  const tokenResource = findIncluded(document, ACCESS_TOKENS_TYPE)
  const tokenAttributes = tokenResource?.attributes as AccessTokenAttributes | undefined

  if (!tokenAttributes?.access_token) {
    throw new Error('Session response did not include an access token')
  }

  const userResource = findIncluded(document, USERS_TYPE)
  const userId = relatedId(document.data, 'user') ?? userResource?.id

  if (!userId) throw new Error('Session response did not identify a user')

  return {
    accessToken: tokenAttributes.access_token,
    expiresInSeconds: tokenAttributes.expires_in_seconds,
    userId,
    user: userResource?.attributes ? toUser(userResource) : null,
  }
}

/**
 * Log in with whichever credential the caller has. The session that comes
 * back is the same shape either way, though a parent signing in a child gets
 * the child's, not their own.
 */
export async function createSession(
  credentials: Credentials,
  options: RequestOptions = {},
): Promise<SessionResult> {
  const document = await apiCreate<Document<Resource>>(
    '/sessions',
    toSessionResource(credentials),
    options,
  )

  return normaliseSessionResponse(document)
}
